import editorialStyle from "../../../components/editorial/style.scss";

import { Fragment } from "preact";
import Link, {
  ProductLink,
  Button,
  ButtonList,
  ButtonBar,
  LargeViewOnly,
  StickyButtons,
  affiliateMerchants,
} from "../../../components/link";

import ProductPrice from "../../../components/product/price";

import AffiliateDisclosure from "../../../components/affiliate-disclosure";
import Center from "../../../components/center";
import Editorial from "../../../components/editorial";
import FixedTable from "../../../components/fixed-table";
import Head from "../../../components/head";
import Hero from "../../../components/hero";
import ImageSet from "../../../components/image-set";
import Image from "../../../components/image";
import Tags from "../../../components/tags";
import Author from "../../../components/author";
import EcoFlowCouponCode from "../components/coupon-code";
import NoWrap from "../../../components/no-wrap";
import SlugHeading from "../../../components/slug-heading";
import SlugLink from "../../../components/slug-link";
import Accordion from "../../../components/accordion";
import AccordionPreview from "../../../components/accordion/preview";
import PhotoListItem from "../../../components/photo-list-item";
import ReviewItem from "../../../components/review-item";
import Questions from "../../../components/questions";
import namedRoutes from "../../../settings/routes";
import { lookupByUrl } from "../../../settings/route-data-lookup";

import AffiliateAd from "../../../components/affiliate-ad";

let headerInfo = lookupByUrl(namedRoutes.ecoflow.titanSeries);

const Page = () => (
  <div>
    <Head data={headerInfo} />
    <Hero
      positionX={0}
      positionY={58}
      background="/images/backgrounds/ecoflow-titan-5000-series.webp"
      titlePrefix="EcoFlow"
      title="TITAN 5000 Series"
      subTitle="Standard, Classic, & Plus Models"
    />
    <Editorial>
      <Center>
        <Author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />

        <LargeViewOnly>
          <ButtonList>
            <Button
              size="medium"
              affiliateData={affiliateMerchants.ecoflow}
              href="https://www.ecoflow.com/us/ecoflow-titan-series"
              icon="EcoFlow"
            >
              View TITAN Series
            </Button>
            {/* <Button
              size="medium"
              affiliate="https://amzn.to/45gpfWv"
              href="https://www.amazon.com/dp/B0G15GWNZQ/"
              secondary
              icon="Amazon"
            >
              View on Amazon
            </Button> */}
          </ButtonList>
        </LargeViewOnly>

        <p>
          <strong>
            Is the EcoFlow TITAN 5000 Series the new sweet spot for affordable
            home battery backup?
          </strong>{" "}
          Each model combines an integrated 5kWh LFP battery, expansion up to
          30.1kWh, and the promise of lower-cost whole-home backup. But does it
          deliver on that promise? Significant differences in output power,
          solar input, and 120V versus 240V split-phase support mean choosing
          between the TITAN 5000 Classic, TITAN 5000, and TITAN 5000 Plus
          requires a closer look.
        </p>
        <p>
          Which model offers the best value once you factor in the required
          accessories? Below, I compare the three TITAN 5000 models, total
          system costs, alternative EcoFlow systems, and competing options from
          other brands.
        </p>

        <AffiliateDisclosure />
      </Center>
      <Center>
        <SlugHeading tag="h2">Highlights</SlugHeading>
        <ul>
          <li>
            <strong>Affordable Whole-Home Backup:</strong> Starting under $3K
            with an integrated 5kWh battery, the TITAN Series offers a
            lower-cost entry point into expandable home battery backup.
          </li>
          <li>
            <strong>Modular Expansion:</strong> Each TITAN model includes an
            integrated 5kWh LFP battery and supports simple expansion up to
            30.1kWh.
          </li>
          <li>
            <strong>Automatic Home Backup:</strong> When integrated with the
            Smart Home Panel 10, the TITAN Series can automatically switch
            connected home circuits to battery power during an outage in under
            20 milliseconds keeping sensitive devices powered on.
          </li>
          <li>
            <strong>Split-Phase Output:</strong> The TITAN 5000 and TITAN 5000
            Plus support 120V/240V split-phase output, allowing these models to
            power compatible heavy-duty loads such as central air conditioners,
            well pumps, clothes dryers, and electric ranges.
          </li>
          <li>
            <strong>High-Capacity Solar Charging:</strong> The lineup supports
            3,200W on the Classic, 4,000W on the TITAN 5000, and 8,000W across
            two MPPTs on the Plus, allowing for large solar arrays and faster
            recharging.
          </li>
          <li>
            <strong>Long-Lasting LFP Battery:</strong> LFP battery chemistry is
            safer and longer lasting than many other lithium-ion chemistries.
            EcoFlow rates the TITAN Series for up to 10 years of use and
            includes a five-year warranty.
          </li>
        </ul>
      </Center>
      <Center>
        <SlugHeading tag="h2">Detailed Comparison</SlugHeading>
        <p>Here’s a side‑by‑side breakdown of the TITAN Series models:</p>

        <Fragment>
          <FixedTable>
            <table>
              <thead>
                <tr>
                  <th class={editorialStyle.sticky}>Model</th>
                  <th>TITAN 5000 Classic</th>
                  <th>TITAN 5000</th>
                  <th>TITAN 5000 Plus</th>
                </tr>
              </thead>
              <tbody>
                <tr class={editorialStyle.noHover}>
                  <th class={editorialStyle.sticky}>
                    <strong>Photo</strong>
                  </th>
                  <td>
                    <Image
                      src="/images/products/ecoflow/titan/titan-5000-classic/front.webp"
                      alt="EcoFlow TITAN 5000 Classic"
                      showAlt
                      maxHeight={200}
                    />
                  </td>
                  <td>
                    <Image
                      src="/images/products/ecoflow/titan/titan-5000/front.webp"
                      alt="EcoFlow TITAN 5000"
                      showAlt
                      maxHeight={200}
                    />
                  </td>
                  <td>
                    <Image
                      src="/images/products/ecoflow/titan/titan-5000-plus/front.webp"
                      alt="EcoFlow TITAN 5000 Plus"
                      showAlt
                      maxHeight={200}
                    />
                  </td>
                </tr>
                <tr class={editorialStyle.noHover}>
                  <th class={editorialStyle.sticky}>
                    <strong>Links</strong>
                  </th>
                  <td>
                    <ButtonBar>
                      <Button
                        size="small"
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/products/ecoflow-titan-5000-classic?variant=55882168991817"
                        icon="EcoFlow"
                      >
                        Reserve Now
                      </Button>
                      {/* <Button
                        size="small"
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://manuals.ecoflow.com/us/product/titan-5000-classic?lang=en_US"
                        secondary
                      >
                        Manual
                      </Button> */}
                    </ButtonBar>
                  </td>
                  <td>
                    <ButtonBar>
                      <Button
                        size="small"
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/products/ecoflow-titan-5000?variant=55882157654089"
                        icon="EcoFlow"
                      >
                        Reserve Now
                      </Button>
                      {/* <Button
                        size="small"
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://manuals.ecoflow.com/us/product/titan-5000?lang=en_US"
                        secondary
                      >
                        Manual
                      </Button> */}
                    </ButtonBar>
                  </td>
                  <td>
                    <ButtonBar>
                      <Button
                        size="small"
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/products/ecoflow-titan-5000-plus?variant=55882164961353"
                        icon="EcoFlow"
                      >
                        Reserve Now
                      </Button>
                      {/* <Button
                        size="small"
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://manuals.ecoflow.com/us/product/titan-5000-plus?lang=en_US"
                        secondary
                      >
                        Manual
                      </Button> */}
                    </ButtonBar>
                  </td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Price</strong>
                  </th>
                  <td>
                    <ProductPrice brand="EcoFlow" model="TITAN 5000 Classic" />
                  </td>
                  <td>
                    <ProductPrice brand="EcoFlow" model="TITAN 5000" />
                  </td>
                  <td>
                    <ProductPrice brand="EcoFlow" model="TITAN 5000 Plus" />
                  </td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Model</strong>
                  </th>
                  <td>EF-TT-5C-G1</td>
                  <td>EF-TT-5-G1</td>
                  <td>EF-TT-5P-G1</td>
                </tr>

                <tr class={editorialStyle.rowBorder}>
                  <th class={editorialStyle.sticky}>
                    <strong>Capacity</strong>
                  </th>
                  <td>5,017.6Wh</td>
                  <td>5,017.6Wh</td>
                  <td>5,017.6Wh</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Expandable</strong>
                  </th>
                  <td>30.1kWh</td>
                  <td>30.1kWh</td>
                  <td>30.1kWh</td>
                </tr>

                <tr class={editorialStyle.rowBorder}>
                  <th class={editorialStyle.sticky}>
                    <strong>AC Output Total</strong>
                  </th>
                  <td>4,000W</td>
                  <td>7,200W</td>
                  <td>7,200W</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>PV Boost</strong> (AC + Solar)
                  </th>
                  <td>no</td>
                  <td>no</td>
                  <td>10,000W</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>X-Boost</strong>
                  </th>
                  <td>
                    5,000W{" "}
                    <sup>
                      <mark>1</mark>
                    </sup>
                  </td>
                  <td>no</td>
                  <td>
                    X-Boost 2.0
                    <sup>
                      <mark>1</mark>
                    </sup>
                  </td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Bypass Mode</strong>
                  </th>
                  <td>Yes</td>
                  <td>Yes</td>
                  <td>Yes</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Split-Phase</strong>
                  </th>
                  <td>No</td>
                  <td>Yes</td>
                  <td>Yes</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Standard Outlets</strong> (120V)
                  </th>
                  <td>
                    3× NEMA 5-20R <br />
                    20A each
                  </td>
                  <td>
                    3× NEMA 5-20R <br />
                    20A each
                  </td>
                  <td>
                    2× NEMA 5-20R <br />
                    20A each
                  </td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Heavy-Duty Outlet</strong>
                  </th>
                  <td>
                    1x NEMA TT-30R <br />
                    120V~30A
                  </td>
                  <td>
                    1x NEMA L14-30R <br />
                    120/240V~30A
                  </td>
                  <td>
                    1x NEMA L14-50R <br />
                    120/240V~41.7A
                  </td>
                </tr>
                <tr class={editorialStyle.rowBorder}>
                  <th class={editorialStyle.sticky}>
                    <strong>Charging Wall Outlet</strong>
                  </th>
                  <td>100-120V~15A, 1800W</td>
                  <td>100-120V~15A, 1800W</td>
                  <td>100-120V~15A, 1800W</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Charging AC In/Out Port</strong>
                  </th>
                  <td>120V~33.3A, 4,000W</td>
                  <td>120/240V~30A, 7,200W</td>
                  <td>120/240V~41.7A, 7,660W</td>
                </tr>

                <tr class={editorialStyle.rowBorder}>
                  <th class={editorialStyle.sticky}>
                    <strong>
                      <NoWrap>Solar Input 1</NoWrap>
                    </strong>
                  </th>
                  <td>30-250V⎓16A, 3,200W</td>
                  <td>
                    40-450V⎓16A, 4,000W{" "}
                    <sup>
                      <mark>1</mark>, 2
                    </sup>
                  </td>
                  <td>
                    40-450V⎓16A, 4,000W <sup>2</sup>
                  </td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>
                      <NoWrap>Solar Input 2</NoWrap>
                    </strong>
                  </th>
                  <td>--</td>
                  <td>--</td>
                  <td>
                    40-450V⎓16A, 4,000W <sup>2</sup>
                  </td>
                </tr>

                <tr class={editorialStyle.rowBorder}>
                  <th class={editorialStyle.sticky}>
                    <strong>USB A</strong>
                  </th>
                  <td>18W × 1</td>
                  <td>18W × 1</td>
                  <td>None</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>USB C</strong>
                  </th>
                  <td>60W × 1</td>
                  <td>60W × 1</td>
                  <td>None</td>
                </tr>

                <tr class={editorialStyle.rowBorder}>
                  <th class={editorialStyle.sticky}>
                    <strong>Weight</strong>
                  </th>
                  <td>110.2 lb (46.5kg)</td>
                  <td>119 lb (54kg)</td>
                  <td>121.3 lb (55kg)</td>
                </tr>
              </tbody>
            </table>
          </FixedTable>
          <div>
            <p class="caption">
              <strong>1.</strong> Indicates conflicting information in official
              product manuals (
              <Link
                affiliateData={affiliateMerchants.ecoflow}
                href="https://manuals.ecoflow.com/us/product/titan-5000-classic?lang=en_US"
              >
                Classic
              </Link>
              ,{" "}
              <Link
                affiliateData={affiliateMerchants.ecoflow}
                href="https://manuals.ecoflow.com/us/product/titan-5000?lang=en_US"
              >
                Standard
              </Link>
              ,{" "}
              <Link
                affiliateData={affiliateMerchants.ecoflow}
                href="https://manuals.ecoflow.com/us/product/titan-5000-plus?lang=en_US"
              >
                Plus
              </Link>
              ) vs EcoFlow marketing sites (
              <Link
                affiliateData={affiliateMerchants.ecoflow}
                href="https://www.ecoflow.com/us/ecoflow-titan-series"
              >
                Series
              </Link>
              ,{" "}
              <ProductLink brand="EcoFlow" model="TITAN 5000 Classic">
                Classic
              </ProductLink>
              ,{" "}
              <ProductLink brand="EcoFlow" model="TITAN 5000">
                Standard
              </ProductLink>
              ,{" "}
              <ProductLink brand="EcoFlow" model="TITAN 5000 Plus">
                Plus
              </ProductLink>
              ). <strong>2.</strong> Requires 60V to start charging.
            </p>
          </div>
        </Fragment>
      </Center>
      {/* <Center>
        <SlugHeading tag="h2">Considerations</SlugHeading>
        <ul>
          <li>
            <strong>120V-Only Classic:</strong> The TITAN 5000 Classic does not
            support 240V loads.
          </li>
          <li>
            <strong>Solar Required for 10,000W:</strong> The TITAN 5000 Plus
            provides 7,200W from its battery and requires sufficient solar input
            to reach 10,000W with PV Boost.
          </li>
          <li>
            <strong>No Built-In USB Ports on Plus:</strong> The TITAN 5000 Plus
            omits USB-A and USB-C ports, although USB devices can still be
            powered through an AC adapter.
          </li>
          <li>
            <strong>Smart Home Panel Required for Panel Integration:</strong>{" "}
            Connecting TITAN to home circuits requires the Smart Home Panel 10.
            Installation may also require permits and professional electrical
            work, depending on local regulations.
          </li>
          <li>
            <strong>Pricing Still Incomplete:</strong> Final TITAN pricing and
            accessory prices have not been announced and could impact the
            overall value of the system.
          </li>
          <li>
            <strong>Conflicting Official Specifications:</strong> EcoFlow’s
            manuals and marketing pages disagree on a few specifications
            (highlighed above). Buyers who depend on those capabilities may want
            to wait for EcoFlow to clarify them.
          </li>
          <li>
            <strong>Moving Homes Claim:</strong> EcoFlow says TITAN can move
            households with you, but relocating an installed Smart Home Panel
            may still require an electrician and new permits.
          </li>
          <li>
            <strong>Heavy Components:</strong> Each expansion battery weighs
            82.7lbs (37.5kg), while the TITAN units weigh approximately
            110-120lbs (46.5-55kg). Keep this in mind when stacking and
            positioning the system.
          </li>
          <li>
            <strong>No Included Solar Disconnect:</strong> Doesnt include a PV
            disconnect switch like the DELTA Pro Ultra X that includes the
            EcoFlow PS Switch Box. This is highly recomended for high voltage
            solar. You can buy a 3rd party device but I would like to see
            EcoFlow offer this accessory for purchase separatly.
          </li>
        </ul>
      </Center> */}
      <Center>
        <SlugHeading tag="h2">Which TITAN 5000 Is Right for You?</SlugHeading>
        <ul>
          <li>
            <strong>
              <ProductLink brand="EcoFlow" model="TITAN 5000 Classic" />{" "}
              (Essential Backup):
            </strong>{" "}
            A lower-cost entry point for essential household circuits, off-grid
            cabins, and some RV setups. Its 4,000W continuous output, 3,200W
            solar input, and NEMA TT-30R RV outlet cover common 120V loads
            without requiring 240V split-phase integration. It also includes two
            front-facing USB ports.{" "}
            <ProductLink brand="EcoFlow" model="TITAN 5000 Classic">
              <NoWrap>Reserve Now</NoWrap>
            </ProductLink>
          </li>
          <li>
            <strong>
              <ProductLink brand="EcoFlow" model="TITAN 5000" /> (Everyday
              Whole-Home Backup):
            </strong>{" "}
            The likely sweet spot for homes needing 120V/240V split-phase
            backup. It combines 7,200W continuous output, a 4,000W high-voltage
            solar input, a NEMA L14-30R outlet, and two front-facing USB ports.
            That makes it suitable for compatible 240V loads such as central air
            conditioners, well pumps, clothes dryers, and electric ranges,
            depending on their running and startup requirements.{" "}
            <ProductLink brand="EcoFlow" model="TITAN 5000">
              <NoWrap>Reserve Now</NoWrap>
            </ProductLink>
          </li>
          <li>
            <strong>
              <ProductLink brand="EcoFlow" model="TITAN 5000 Plus" /> (Maximum
              Power and Solar):
            </strong>{" "}
            Built for heavier loads and larger solar arrays. It provides 7,200W
            of continuous battery output, up to 10,000W with PV Boost, dual
            solar inputs supporting up to 8,000W total, and a NEMA 14-50R outlet
            (41.7A). These upgrades give it the highest output and solar
            charging capability in the TITAN 5000 lineup.{" "}
            <ProductLink brand="EcoFlow" model="TITAN 5000 Plus">
              <NoWrap>Reserve Now</NoWrap>
            </ProductLink>
          </li>
        </ul>

        <Accordion
          headingsAs="h3"
          items={[
            {
              heading: "Other EcoFlow Systems to Consider",
              content: (
                <Fragment>
                  <ul class="none">
                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/products/delta-pro-ultra?variant=40758830071881"
                        img="/images/products/ecoflow/delta-pro-ultra/front.webp"
                        title="DELTA Pro Ultra"
                        description={
                          <Fragment>
                            High-capacity modular capacity 6kWh-90kWh and
                            scalable 7.2-21.6kW output provides outstanding
                            expansion flexibility, though total system pricing
                            quickly climbs above its $4K starting price to $12K+
                            depending on configurations.
                          </Fragment>
                        }
                        buttonText="EcoFlow"
                        buttonIcon="EcoFlow"
                      />
                    </li>
                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/products/delta-pro-ultra-x?variant=54714781171785"
                        img="/images/products/ecoflow/delta-pro-ultra-x/front-right.webp"
                        title="DELTA Pro Ultra X"
                        description={
                          <Fragment>
                            High-capacity modular storage from 12–180kWh and
                            scalable 12–36kW output provide substantially more
                            power for heavy 240V residential loads, though its
                            $8K starting price represents a significant upfront
                            investment.
                          </Fragment>
                        }
                        buttonText="EcoFlow"
                        buttonIcon="EcoFlow"
                      />
                    </li>
                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/products/delta-pro-3-portable-power-station?variant=41385721004105"
                        img="/images/products/ecoflow/delta-pro-3/front.webp"
                        title="DELTA Pro 3"
                        description={
                          <Fragment>
                            A portable 4kWh system with 4,000W continuous output
                            and selectable 120V or 240V operation, making it
                            well suited to mobile setups and smaller home backup
                            systems.
                          </Fragment>
                        }
                        buttonText="EcoFlow"
                        buttonIcon="EcoFlow"
                      />
                    </li>
                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://energy.ecoflow.com/us/products/ocean-pro-home-solar-battery"
                        img="/images/products/ecoflow/ocean-pro/front.webp"
                        title="OCEAN Pro"
                        description={
                          <Fragment>
                            Requires professional installation but offers
                            modular capacity from 10–80kWh and up to 40kW of
                            solar input for large-scale home energy storage.
                          </Fragment>
                        }
                        buttonText="EcoFlow"
                        buttonIcon="EcoFlow"
                      />
                    </li>
                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://www.ecoflow.com/us/stream-ultra-home-solar-system"
                        img="/images/products/ecoflow/stream/stream-ultra-pro/front-right.webp"
                        title="STREAM Ultra"
                        description={
                          <Fragment>
                            A plug-and-play solar battery for simple home
                            installations, with 1.92kWh of storage and up to
                            2,000W of solar input. It can power essential
                            appliances but is not designed for heavy whole-home
                            backup. Availability and installation requirements
                            vary by market.
                          </Fragment>
                        }
                        buttonText="EcoFlow"
                        buttonIcon="EcoFlow"
                      />
                    </li>
                  </ul>
                </Fragment>
              ),
            },
          ]}
        />

        <AffiliateAd brand="EcoFlow" size="billboard" />

        <p>
          <strong>Considering other brands?</strong> Check out the{" "}
          <Link
            affiliate="https://link.amazon/B05zA0Sxc"
            href="https://www.amazon.com/dp/B0GFDFVJFM/"
          >
            Anker SOLIX E10 + Smart Inlet Box
          </Link>
          ,{" "}
          <Link
            affiliateData={affiliateMerchants.jackery}
            href="https://www.jackery.com/products/jackery-solar-generator-5000-plus?variant=41317175459927"
          >
            Jackery Explorer 5000 Plus + Smart Transfer Switch
          </Link>
          , and{" "}
          <Link
            affiliateData={affiliateMerchants.bluetti}
            href="https://www.bluettipower.com/products/apex-300-b300k-home-backup-kit-single-apex-300?variant=47914968252635&id=692035f018e3fa2d3cee262e"
          >
            BLUETTI Apex 300 + Home Backup Kit
          </Link>
          .
        </p>

        <Accordion
          headingsAs="h3"
          items={[
            {
              heading: "Not needing whole-home backup?",
              content: (
                <Fragment>
                  <h4>Smaller portable units:</h4>
                  <ul class="none">
                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/products/delta-3-plus-portable-power-station?variant=41826182496329"
                        img="/images/products/ecoflow/delta-3-plus/front-right.webp"
                        title="EcoFlow DELTA 3 Plus"
                        description={<Fragment></Fragment>}
                        buttonText="EcoFlow"
                        buttonIcon="EcoFlow"
                      />
                    </li>
                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/products/delta-3-ultra-series-portable-power-station?variant=54718705008713&view=d3u"
                        img="/images/products/ecoflow/delta-3-ultra-plus/front-right.webp"
                        title="EcoFlow DELTA 3 Ultra Plus"
                        description={<Fragment></Fragment>}
                        buttonText="EcoFlow"
                        buttonIcon="EcoFlow"
                      />
                    </li>
                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/products/delta-pro-3-portable-power-station?variant=41385721004105"
                        img="/images/products/ecoflow/delta-pro-3/front.webp"
                        title="EcoFlow DELTA Pro 3"
                        description={<Fragment></Fragment>}
                        buttonText="EcoFlow"
                        buttonIcon="EcoFlow"
                      />
                    </li>
                  </ul>

                  <h4>Designed for refrigerators:</h4>
                  <ul class="none">
                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/fridgepower-battery-backup?id=6a2a69d42b71fb8ff494a178&variant=48862781210843"
                        img="/images/products/bluetti/fridgepower/front.webp"
                        title="BLUETTI FridgePower"
                        description={<Fragment></Fragment>}
                        buttonText="BLUETTI"
                        buttonIcon="Bluetti"
                      />
                    </li>
                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.jackery}
                        href="https://www.jackery.com/products/jackery-fridge-guard"
                        img="/images/products/jackery/fridgeguard/front.webp?v=2"
                        title="Jackery FridgeGuard"
                        description={<Fragment></Fragment>}
                        buttonText="Jackery"
                        buttonIcon="Jackery"
                      />
                    </li>
                  </ul>
                </Fragment>
              ),
            },
          ]}
        />
      </Center>
      <Center>
        <SlugHeading tag="h2">TITAN Accessories</SlugHeading>
        <p>
          Official accessories designed to integrate, adapt, and expand your
          TITAN setup:
        </p>

        <ul class="none">
          <li>
            <PhotoListItem
              affiliate="https://link.amazon/B099kI15o"
              href="https://www.amazon.com/dp/B0HJZFV2V8/"
              img="/images/products/ecoflow/titan/titan-battery-5kwh/front.webp"
              title="TITAN Battery 5kWh"
              description={
                <Fragment>
                  Connect up to 5 to increase the capacity of TITAN (
                  <Link
                    affiliateData={affiliateMerchants.ecoflow}
                    href="https://manuals.ecoflow.com/us/product/titan-5000-battery?lang=en_US"
                  >
                    see manual
                  </Link>
                  ).
                </Fragment>
              }
              buttonText="Amazon"
              buttonIcon="Amazon"
            />
          </li>
          <li>
            <PhotoListItem
              affiliate="https://link.amazon/B097bICEZ"
              href="https://www.amazon.com/dp/B0HJZK2NSC/"
              img="/images/products/ecoflow/home/smart-home-panel-10/front.webp"
              title="Smart Home Panel 10"
              titleSuffix="(120V)"
              description={
                <Fragment>
                  Integrates with circuit panels for automated home backup of
                  120V circuits.
                </Fragment>
              }
              buttonText="Amazon"
              buttonIcon="Amazon"
            />
          </li>
          <li>
            <PhotoListItem
              affiliate="https://link.amazon/B0glYyw85"
              href="https://www.amazon.com/dp/B0HJZCWMCP/"
              img="/images/products/ecoflow/home/smart-home-panel-10/front.webp"
              title="Smart Home Panel 10"
              titleSuffix="(120V/240V)"
              description={
                <Fragment>
                  Integrates with circuit panels for automated home backup of
                  120V and 240V circuits.
                </Fragment>
              }
              buttonText="Amazon"
              buttonIcon="Amazon"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/master-generator-rectifier?variant=54714823114825"
              img="/images/products/ecoflow/accessories/flowmaster-generator-rectifier.webp"
              title="FlowMaster Generator Rectifier"
              description={
                <Fragment>Converts generator AC into stable DC power.</Fragment>
              }
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href=""
              img="/images/products/ecoflow/titan/5000-series/portable-stand.webp"
              title="Portable Stand"
              titleSuffix="(TITAN Series)"
              description={
                <Fragment>
                  Support and transport TITAN and batteries with ease.
                </Fragment>
              }
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/ac-generator-charging-adapter?variant=40767961530441"
              img="/images/products/ecoflow/accessories/ac-generator-charging-adapter.webp"
              title="AC Generator Charging Adapter"
              description={
                <Fragment>
                  Charge using generators with a NEMA L14-30R port.
                </Fragment>
              }
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
          {/* <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/ev-x-stream-adapter-delta-pro-ultra?variant=40758561964105"
              img="/images/products/ecoflow/accessories/ev-x-stream-adapter-delta-pro-ultra.webp"
              title="EV X-Stream Adapter"
              description={
                <Fragment>
                  7200W charging via EV station (J1772 connector)
                </Fragment>
              }
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li> */}
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/powerpulse-ev-charger?variant=54475840061513"
              img="/images/products/ecoflow/home/powerpulse-ev-charger/front.webp"
              title="PowerPulse EV Charger"
              titleSuffix="(9.6kW)"
              description={
                <Fragment>Enables EV charging integration.</Fragment>
              }
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href=""
              img="/images/products/ecoflow/titan/5000-series/solar-to-hp500-charging-cable.webp"
              title="Solar to HP500 Charging Cable"
              titleSuffix="(Included)"
              description={
                <Fragment>
                  Connect solar panels to TITAN power station's proprietary
                  HP500 port. Each TITAN comes with a single HP500 cable.
                </Fragment>
              }
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
        </ul>

        <p class="caption-left">
          Additional accessories may become available. More info coming soon.
        </p>
      </Center>
      <Center>
        <SlugHeading tag="h2">Summary</SlugHeading>
        <p>
          The EcoFlow <strong>TITAN 5000 Series</strong> bridges the gap between
          the <strong>portable DELTA Pro lineup</strong> and the{" "}
          <strong>more expensive DELTA Pro Ultra ecosystem</strong>. Each model
          starts with a 5kWh battery and expands up to 30.1kWh, but differences
          in output, solar input, and split-phase support give buyers three
          distinct levels of home backup capability.
        </p>
        <p>
          Starting at{" "}
          <ProductPrice brand="EcoFlow" model="TITAN 5000 Classic" /> for the{" "}
          <ProductLink brand="EcoFlow" model="TITAN 5000 Classic" />,{" "}
          <ProductPrice brand="EcoFlow" model="TITAN 5000" /> for the{" "}
          <ProductLink brand="EcoFlow" model="TITAN 5000" />, and{" "}
          <ProductPrice brand="EcoFlow" model="TITAN 5000 Plus" /> for the{" "}
          <ProductLink brand="EcoFlow" model="TITAN 5000 Plus" />, the{" "}
          <Link
            affiliateData={affiliateMerchants.ecoflow}
            href="https://www.ecoflow.com/us/ecoflow-titan-series"
          >
            TITAN Series
          </Link>{" "}
          significantly lowers the cost of entry for homeowners seeking a
          scalable whole-home backup solution.
        </p>
        <p>
          Follow{" "}
          <Link external href="https://www.facebook.com/TysonsGearLab">
            Tyson's Gear Lab
          </Link>{" "}
          on Facebook for the latest pricing, specifications, and release
          updates.
        </p>

        <EcoFlowCouponCode
          href="https://us.ecoflow.com/"
          buttonText="EcoFlow Store"
        />

        <ButtonList>
          <Button
            affiliateData={affiliateMerchants.ecoflow}
            href="https://www.ecoflow.com/us/ecoflow-titan-series"
            icon="EcoFlow"
          >
            View TITAN Series
          </Button>
          <Button
            affiliate="https://link.amazon/B08qKpnoZ"
            href="https://www.amazon.com/dp/B0HN1MNNH3/"
            secondary
            icon="Amazon"
          >
            Buy on Amazon
          </Button>
        </ButtonList>

        <Tags tags={headerInfo.tags} brands={headerInfo.brands} />
      </Center>
      <Center>
        <h3>Related</h3>
        <ul class="none">
          <li>
            <ReviewItem href={namedRoutes.ecoflow.delta4.series} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.ecoflow.batteryCompatibility} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.ecoflow.vanlife} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.ecoflow.dp3} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.powerSystems} />
          </li>
        </ul>
      </Center>
      <Questions />
      <Center>
        <Author
          author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />
      </Center>
      <StickyButtons
        title="EcoFlow TITAN Series"
        description={
          <Fragment>
            Starting at{" "}
            <ProductPrice brand="EcoFlow" model="TITAN 5000 Classic" />
          </Fragment>
        }
      >
        <Button
          affiliateData={affiliateMerchants.ecoflow}
          href="https://www.ecoflow.com/us/ecoflow-titan-series"
          icon="EcoFlow"
        >
          View on EcoFlow
        </Button>
        {/* <Button
          affiliate="https://amzn.to/45gpfWv"
          href="https://www.amazon.com/dp/B0G15GWNZQ/"
          secondary
          icon="Amazon"
        >
          View on Amazon
        </Button> */}
      </StickyButtons>
    </Editorial>
  </div>
);

export default Page;
